import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MaterialsModule } from '@shared/materials.module';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { AppService } from '@core/services/app/app-api';
import { OrgAndStockroomService } from '@core/services/org-and-stockroom/org-and-stockroom-api';
import { ToastService } from '@core/services/toast-api';

import { SupportTicketService } from '../support-ticket-api';
import {
  SUPPORT_TICKET_USER_INTERFACE,
  SUPPORT_TICKET_USER_INTERFACE_DEFAULT,
  SUPPORT_TICKET_FUNCTIONAL_AREA_WEB,
  SUPPORT_TICKET_FUNCTIONAL_AREA_MOBILE,
  SUPPORT_TICKET_FUNCTIONAL_AREA_BOTH,
  SUPPORT_TICKET_BROWSER,
  SUPPORT_TICKET_MOBILE_MODELS,
  SUPPORT_TICKET_BROWSER_AND_MOBILE_MODELS,
  SUPPORT_TICKET_APPLICATION,
  SUPPORT_TICKET_ISSUE_TYPE,
  SUPPORT_TICKET_SCALE_OF_IMPACT,
  SUPPORT_TICKET_MODULES_LIST,
  SUPPORT_TICKET_CHEMICAL_MANAGER_OPTIONS,
  SUPPORT_TICKET_EQUIPMENT_MANAGER_OPTIONS,
  SUPPORT_TICKET_MAX_FILE_SIZE_MB,
  SUPPORT_TICKET_MAX_FILES,
  SUPPORT_TICKET_ACCEPTED_FILE_EXTENSIONS,
  SUPPORT_TICKET_ACCEPTED_FILE_TYPES,
  SUPPORT_TICKET_ACCEPT_ATTR,
} from '../support-ticket.constants';
import { MatSelect } from '@angular/material/select';
import { AvantorDialogsService } from '@shared/dialogs/avantor-dialogs-api';

/** Single user row added to the "Others" table. */
interface SelectedOtherUser {
  id: string;
  name: string;
  email: string;
  raw: any;
}

/** Single file attached to the "Screenshots of the defect" upload area. */
interface AttachedFile {
  /** Original File object — kept so we can re-upload via FormData in step 2. */
  file: File;
  name: string;
  type: string;
  size: number;
  base64: string;
}

/**
 * Support Ticket form dialog. Layout matches the Figma 1:1.
 *
 * Submit flow is two-step:
 *   1) POST /ev/api/supportTicket/SubmitDefect            (JSON body)
 *   2) POST /ev/api/supportTicket/submitDefect/{id}/screenshots  (multipart files)
 * Step 2 is only fired when (a) step 1 succeeded with a usable ticket id, AND
 * (b) the user actually attached at least one file.
 */
@Component({
  selector: 'app-dialog-support-ticket-form',
  templateUrl: './dialog-support-ticket-form.html',
  styleUrls: ['./dialog-support-ticket-form.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MaterialsModule,
    TranslateModule,
  ],
})
export class DialogSupportTicketFormComponent implements OnInit {
  @ViewChild('orgSelect') orgSelect?: MatSelect;

  titleText = 'Support Ticket Form';
  ticketForm!: FormGroup;
  submitting = false;
  readonly maxIssueOccurredDate = new Date();

  // Data loaded from APIs / session
  organizations: any[] = [];
  stockrooms: any[] = [];
  usersForOthers: any[] = [];
  /** Multiple users selected in the Others flow — each becomes a row in the table. */
  selectedOtherUsers: SelectedOtherUser[] = [];
  locations: any[] = [];

  // Searchable-dropdown filter state
  filteredOrganizations: any[] = [];
  filteredStockrooms: any[] = [];
  filteredUsersForOthers: any[] = [];
  filteredFunctionalAreaOptions: string[] = [];
  filteredModuleScreenOptions: any[] = [];
  orgFilterText = '';
  stockroomFilterText = '';
  userIdFilterText = '';
  functionalAreaFilterText = '';
  moduleScreenFilterText = '';

  // Hard-coded option lists
  userInterfaceOptions = this.sortStrings(SUPPORT_TICKET_USER_INTERFACE);
  browserOptions = this.sortStrings(SUPPORT_TICKET_BROWSER_AND_MOBILE_MODELS);
  applicationOptions = this.sortStrings(SUPPORT_TICKET_APPLICATION);
  issueTypeOptions = this.sortStrings(SUPPORT_TICKET_ISSUE_TYPE);
  scaleOfImpactOptions = this.sortStrings(SUPPORT_TICKET_SCALE_OF_IMPACT);

  /** Module/Screen list — application-dependent. Search-filtered into `filteredModuleScreenOptions`. */
  moduleScreenOptions = this.sortByLabel(SUPPORT_TICKET_MODULES_LIST, m => m.name);

  /** Functional Area list — application-dependent. Inventory Manager also follows User Interface selection. */
  functionalAreaOptions: string[] = this.sortStrings(SUPPORT_TICKET_FUNCTIONAL_AREA_BOTH);

  // ------------------------------------------------------------------------
  // FILE UPLOAD STATE
  // ------------------------------------------------------------------------
  /** All currently-attached files (max 5). */
  attachedFiles: AttachedFile[] = [];
  readonly MAX_FILES = SUPPORT_TICKET_MAX_FILES;
  readonly MAX_FILE_SIZE_MB = SUPPORT_TICKET_MAX_FILE_SIZE_MB;
  readonly ACCEPT_ATTR = SUPPORT_TICKET_ACCEPT_ATTR;
  private fileReadBatch = 0;

  /** True once `attachedFiles.length >= MAX_FILES` — used to fully block clicks/drops. */
  get isUploadFull(): boolean {
    return this.attachedFiles.length >= this.MAX_FILES;
  }

  // ------------------------------------------------------------------------
  // SESSION CONTEXT (used to populate the "loggedIn..." payload fields)
  // ------------------------------------------------------------------------
  /** Captured from session at init — also used to default-select the Org dropdown. */
  private currentLoggedInOrgId: string | null = null;
  /** Captured from session at init — also used to default-select the Stockroom dropdown. */
  private currentLoggedInStockroomId: string | null = null;
  /** Numeric user id captured from the `config` session blob — sent as `reportingUserId`. */
  private currentLoggedInUserId: string | null = null;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<DialogSupportTicketFormComponent>,
    private supportTicketService: SupportTicketService,
    private appService: AppService,
    private orgAndStockroomService: OrgAndStockroomService,
    private toastService: ToastService,
    private router: Router,
    private translatePipe: TranslatePipe,
    private dialogService: AvantorDialogsService
  ) { }

  ngOnInit(): void {
    this.initForm();
    this.bindUserAndOrgContext();
    this.loadOrganizations();
    this.loadLocations();

    // Functional Area options bound for search-filter use
    this.filteredFunctionalAreaOptions = [...this.functionalAreaOptions];
    // Module/Screen options bound for search-filter use
    this.filteredModuleScreenOptions = [...this.moduleScreenOptions];

  }

  // ------------------------------------------------------------------------
  // FORM
  // ------------------------------------------------------------------------

  private initForm(): void {
    this.ticketForm = this.fb.group({
      currentOrgStockroom: [{ value: '', disabled: true }],
      reportingUserId: [{ value: '', disabled: true }],

      orgId: [null],
      stockroomId: [null],
      submittingFor: ['MYSELF'],
      /** Stored as the location `id` (numeric). Display text is `locationName`. */
      location: [null],

      // Single-select: 'Web' | 'Mobile' | 'Both'
      userInterface: [SUPPORT_TICKET_USER_INTERFACE_DEFAULT, Validators.required],
      // Multi-select arrays
      functionalArea: [[]],
      moduleScreens: [[]],

      browser: [[SUPPORT_TICKET_BROWSER[0]]],
      application: [SUPPORT_TICKET_APPLICATION[0], Validators.required],

      issueOccursForAllUsers: [false, Validators.required],
      issueOccurredDate: [new Date(), Validators.required],
      issueType: [SUPPORT_TICKET_ISSUE_TYPE[0]],
      scaleOfImpact: [SUPPORT_TICKET_SCALE_OF_IMPACT[0]],

      defectTitle: ['', [Validators.required, Validators.maxLength(500)]],
      defectDescription: ['', [Validators.required, Validators.maxLength(5000)]],
      defectStepsToReproduce: ['', Validators.maxLength(5000)],
    });

    this.ticketForm.get('submittingFor')?.valueChanges.subscribe((value) => {
      this.onSubmittingForChange(value);
    });
  }

  /**
   * Pulls logged-in user / org / stockroom from session storage.
   *
   * Per user spec: org/stockroom names come from `cStockroomAndOrg` session key
   * (read via OrgAndStockroomService.getStockroomAndOrgName()) — fields are
   * `organization.orgName` and `stockroom?.stockroomName`.
   *
   * Reporting User Id (numeric) + display email come from the `config` session key
   * (read via AppService.getUserData()).
   */
  private bindUserAndOrgContext(): void {
    // ---- 1) Org + Stockroom names from cStockroomAndOrg session key ----
    let orgName = '';
    let stockroomName = '';
    let currentOrgId: string | null = null;

    const cStockroomAndOrgRaw = this.orgAndStockroomService.getStockroomAndOrgName();
    if (cStockroomAndOrgRaw) {
      try {
        const parsed: any = JSON.parse(cStockroomAndOrgRaw);
        // eslint-disable-next-line no-console
        console.log('[SupportTicket] cStockroomAndOrg parsed:', parsed);
        orgName = parsed?.organization?.orgName || '';
        stockroomName = parsed?.stockroom?.stockroomName || '';
        currentOrgId = parsed?.organization?.id
          || parsed?.organization?.orgId
          || parsed?.organization?.organizationId
          || null;
      } catch (e) {
        console.warn('[SupportTicket] could not parse cStockroomAndOrg', e);
      }
    }

    // Also try the IDs-only key as a fallback for currentOrgId
    if (!currentOrgId) {
      const idsRaw = this.orgAndStockroomService.getStockroomAndOrg();
      if (idsRaw) {
        try {
          const parsedIds: any = JSON.parse(idsRaw);
          currentOrgId = parsedIds?.orgId || null;
          this.currentLoggedInStockroomId = parsedIds?.stockroomId || null;
        } catch (e) { /* ignore */ }
      }
    } else {
      // currentOrgId came from cStockroomAndOrg — also pick stockroomId from same source
      const idsRaw = this.orgAndStockroomService.getStockroomAndOrg();
      if (idsRaw) {
        try {
          const parsedIds: any = JSON.parse(idsRaw);
          this.currentLoggedInStockroomId = parsedIds?.stockroomId || null;
        } catch (e) { /* ignore */ }
      }
    }
    this.currentLoggedInOrgId = currentOrgId;

    const display = [orgName, stockroomName].filter(Boolean).join(' - ');

    // ---- 2) Reporting User Id (numeric) + display email from `config` session key ----
    const u = this.appService.getUserData() || {};
    // eslint-disable-next-line no-console
    console.log('[SupportTicket] user/login (config) data:', u);

    // Numeric user id — try a defensive chain since different APIs use different keys
    this.currentLoggedInUserId = u.userId || u.id || u.internalUserId || u.userInternalId || null;

    this.ticketForm.patchValue({
      currentOrgStockroom: display,
      reportingUserId: u.email || u.loginId || u.userId || '',
    });
  }

  // ------------------------------------------------------------------------
  // LOCATIONS API
  // ------------------------------------------------------------------------

  /**
   * Loads locations from api/stockroom/locations. Response shape:
   *   { data: [{ id, locationName, ... }, ...], totalCount, count }
   * The Location dropdown displays `locationName` and submits `id` (per the
   * backend payload sample where location is a stringified numeric id).
   * Default = first item.
   */
  private async loadLocations(): Promise<void> {
    try {
      const res = await this.supportTicketService.getLocations();
      // eslint-disable-next-line no-console
      console.log('[SupportTicket] getLocations response:', res);
      const list = Array.isArray(res?.data) ? res.data : this.extractList(res);
      this.locations = this.sortByLabel((list || []).filter((l: any) => l && l.locationName), l => l.locationName);

      if (this.locations.length) {
        const first = this.locations[0];
        // Form value = location id (sent in payload). Display text = locationName.
        this.ticketForm.patchValue({ location: first.id ?? first.locationId ?? null });
      } else {
        this.ticketForm.patchValue({ location: null });
      }
    } catch (err) {
      console.error('[SupportTicket] loadLocations error', err);
      this.locations = [];
    }
  }

  /** Normalizes various API response shapes into a flat array. */
  private extractList(res: any): any[] {
    if (Array.isArray(res)) return res;
    if (!res || typeof res !== 'object') return [];
    const keys = [
      'imeorganizationBaseDTO',
      'imestockroomBaseDTO',
      'stockroomHierarchy',
      'imeuserBaseDTO',
      'data', 'organizations', 'stockrooms', 'users', 'items', 'results',
      'organizationList', 'stockroomList', 'userList', 'list',
    ];
    for (const k of keys) {
      if (Array.isArray(res[k])) return res[k];
      if (res[k] && typeof res[k] === 'object') {
        for (const k2 of keys) {
          if (Array.isArray(res[k][k2])) return res[k][k2];
        }

        const nestedValues = Object.values(res[k]);
        const nestedArray = nestedValues.find(Array.isArray) as any[] | undefined;
        if (nestedArray) return nestedArray;
        if (nestedValues.length && nestedValues.every((item: any) => item && typeof item === 'object')) {
          return nestedValues;
        }
      }
    }

    const values = Object.values(res);
    const arrayValue = values.find(Array.isArray) as any[] | undefined;
    if (arrayValue) return arrayValue;
    if (values.length && values.every((item: any) => item && typeof item === 'object')) {
      return values;
    }

    return [];
  }

  // ------------------------------------------------------------------------
  // ORG / STOCKROOM
  // ------------------------------------------------------------------------

  private async loadOrganizations(): Promise<void> {
    try {
      const res = await this.supportTicketService.getOrganizations();
      // eslint-disable-next-line no-console
      console.log('[SupportTicket] getOrganizations response:', res);
      this.organizations = this.sortByLabel(this.extractList(res), o => this.getOrganizationName(o));
      this.filteredOrganizations = [...this.organizations];

      if (!this.organizations.length) return;

      let defaultOrg: any = null;
      if (this.currentLoggedInOrgId) {
        defaultOrg = this.organizations.find((o: any) =>
          String(o.id || o.organizationId || o.orgId) === String(this.currentLoggedInOrgId)
        );
      }
      if (!defaultOrg) {
        const cRaw = this.orgAndStockroomService.getStockroomAndOrgName();
        let currentOrgName = '';
        if (cRaw) {
          try { currentOrgName = JSON.parse(cRaw)?.organization?.orgName || ''; } catch { /* ignore */ }
        }
        if (currentOrgName) {
          defaultOrg = this.organizations.find((o: any) =>
            (o.orgName || o.name || o.organizationName) === currentOrgName
          );
        }
      }
      if (!defaultOrg) defaultOrg = this.organizations[0];

      const orgId = defaultOrg.id || defaultOrg.organizationId || defaultOrg.orgId;
      this.ticketForm.patchValue({ orgId });
      if (orgId) this.loadStockrooms(orgId);
    } catch (err) {
      console.error('[SupportTicket] loadOrganizations error', err);
    }
  }

  onOrgChange(orgId: string): void {
    this.clearOtherUserSelection();
    this.usersForOthers = [];
    this.filteredUsersForOthers = [];
    this.loadStockrooms(orgId);

    if (this.ticketForm.get('submittingFor')?.value === 'OTHERS') {
      this.loadUsersForOthers(orgId);
    }
  }

  filterOrg(text: string): void {
    this.orgFilterText = text || '';
    const q = this.orgFilterText.trim().toLowerCase();
    if (!q) {
      this.filteredOrganizations = [...this.organizations];
      return;
    }
    this.filteredOrganizations = this.organizations.filter((o: any) => {
      const name = (o.orgName || o.organizationName || o.name || '').toString().toLowerCase();
      return name.includes(q);
    });
  }

  filterStockroom(text: string): void {
    this.stockroomFilterText = text || '';
    const q = this.stockroomFilterText.trim().toLowerCase();
    if (!q) {
      this.filteredStockrooms = [...this.stockrooms];
      return;
    }
    this.filteredStockrooms = this.stockrooms.filter((s: any) => {
      const name = (s.stockroomName || s.name || '').toString().toLowerCase();
      return name.includes(q);
    });
  }

  onOrgPanelOpened(opened: boolean): void {
    if (opened) {
      this.orgFilterText = '';
      this.filteredOrganizations = [...this.organizations];
      this.resetOrgPanelScroll();
    }
  }

  private resetOrgPanelScroll(): void {
    setTimeout(() => this.scrollOrgPanelToTop());
    setTimeout(() => this.scrollOrgPanelToTop(), 100);
  }

  private scrollOrgPanelToTop(): void {
    const panel = this.orgSelect?.panel?.nativeElement as HTMLElement | undefined;
    if (panel) {
      panel.scrollTop = 0;
    }
  }

  onStockroomPanelOpened(opened: boolean): void {
    if (opened) {
      this.stockroomFilterText = '';
      this.filteredStockrooms = [...this.stockrooms];
    }
  }

  private async loadStockrooms(orgId: string): Promise<void> {
    if (!orgId) {
      this.stockrooms = [];
      this.filteredStockrooms = [];
      return;
    }
    try {
      const res = await this.supportTicketService.getStockrooms(orgId);
      // eslint-disable-next-line no-console
      console.log('[SupportTicket] getStockrooms response:', res);
      const top = this.extractList(res);

      const flat: any[] = [];
      for (const item of top) {
        if (item?.stockroomName) flat.push(item);
        if (Array.isArray(item?.childStockroom)) {
          for (const child of item.childStockroom) {
            if (child?.stockroomName) flat.push(child);
          }
        }
      }
      this.stockrooms = this.sortByLabel(flat, s => this.getStockroomName(s));
      this.filteredStockrooms = [...this.stockrooms];

      if (!this.stockrooms.length) {
        this.ticketForm.patchValue({ stockroomId: null });
        return;
      }

      let defaultSr: any = null;
      if (this.currentLoggedInStockroomId) {
        defaultSr = this.stockrooms.find((s: any) =>
          String(s.id || s.stockroomId) === String(this.currentLoggedInStockroomId)
        );
      }
      if (!defaultSr) {
        const cRaw = this.orgAndStockroomService.getStockroomAndOrgName();
        let currentSrName = '';
        if (cRaw) {
          try { currentSrName = JSON.parse(cRaw)?.stockroom?.stockroomName || ''; } catch { /* ignore */ }
        }
        if (currentSrName) {
          defaultSr = this.stockrooms.find((s: any) =>
            (s.stockroomName || s.name) === currentSrName
          );
        }
      }
      if (!defaultSr) defaultSr = this.stockrooms[0];

      const id = defaultSr.id || defaultSr.stockroomId;
      this.ticketForm.patchValue({ stockroomId: id });
    } catch (err) {
      console.error('[SupportTicket] loadStockrooms error', err);
    }
  }

  // ------------------------------------------------------------------------
  // MYSELF / OTHERS  (multi-user, additive table)
  // ------------------------------------------------------------------------

  onSubmittingForChange(value: string): void {
    this.clearOtherUserSelection();

    if (value === 'OTHERS') {
      this.loadUsersForOthers();
    } else {
      this.usersForOthers = [];
      this.filteredUsersForOthers = [];
    }
  }

  private clearOtherUserSelection(): void {
    this.selectedOtherUsers = [];
    this.userIdFilterText = '';
  }

  private async loadUsersForOthers(orgId: string = this.ticketForm.get('orgId')?.value): Promise<void> {
    if (!orgId) {
      this.usersForOthers = [];
      this.filteredUsersForOthers = [];
      return;
    }

    try {
      const res = await this.supportTicketService.getOrganizationUsers(orgId);
      this.usersForOthers = this.sortByLabel(this.extractList(res), u => this.getUserDisplayName(u));
      this.filteredUsersForOthers = [...this.usersForOthers];
      // eslint-disable-next-line no-console
      console.log('[SupportTicket] organization users loaded:', this.usersForOthers.length, res);
    } catch (err) {
      console.error('[SupportTicket] loadUsersForOthers error', err);
      this.usersForOthers = [];
      this.filteredUsersForOthers = [];
    }
  }

  filterUsersForOthers(text: string): void {
    this.userIdFilterText = text || '';
    const q = this.userIdFilterText.trim().toLowerCase();
    if (!q) {
      this.filteredUsersForOthers = [...this.usersForOthers];
      return;
    }
    this.filteredUsersForOthers = this.usersForOthers.filter((u: any) => {
      const name = this.getUserDisplayName(u).toLowerCase();
      const email = this.getUserEmail(u).toLowerCase();
      return name.includes(q) || email.includes(q);
    });
  }

  onForUserPanelOpened(opened: boolean): void {
    if (opened) {
      this.userIdFilterText = '';
      if (this.ticketForm.get('submittingFor')?.value === 'OTHERS' && !this.usersForOthers.length) {
        this.loadUsersForOthers();
        return;
      }
      this.filteredUsersForOthers = [...this.usersForOthers];
    }
  }

  onForUserChange(userId: string): void {
    if (!userId) return;
    const user = this.usersForOthers.find((u: any) => this.getUserId(u) === String(userId));
    if (!user) return;

    const id = this.getUserId(user);
    if (this.selectedOtherUsers.some(s => s.id === id)) {
      this.toastService.show('info', 'Already added',
        'This user is already in the list.', true, 4000);
      return;
    }

    this.selectedOtherUsers.push({
      id,
      name: this.getUserDisplayName(user),
      email: this.getUserEmail(user) || '-',
      raw: user,
    });
  }

  getUserId(user: any): string {
    return String(user?.skGuid || user?.id || user?.userId || user?.loginId || user?.email || '');
  }

  getUserDisplayName(user: any): string {
    return user?.fullName
      || `${user?.firstName || ''} ${user?.lastName || ''}`.trim()
      || user?.userName
      || user?.loginId
      || user?.email
      || 'User';
  }

  getUserEmail(user: any): string {
    return user?.email || user?.loginId || user?.userEmail || '';
  }

  removeOtherUser(index: number): void {
    if (index < 0 || index >= this.selectedOtherUsers.length) return;
    this.selectedOtherUsers.splice(index, 1);
  }

  // ------------------------------------------------------------------------
  // APPLICATION / USER INTERFACE -> DEPENDENT DROPDOWN LISTS
  // ------------------------------------------------------------------------

  /**
   * `userInterface` is single-select ('Web' | 'Mobile' | 'Both').
   * Browser/Device options switch based on this selection.
   * Inventory Manager functional areas also follow this selection.
   */
  onUserInterfaceChange(value: string): void {
    this.updateBrowserOptions(value);

    const currentBrowsers = this.toArray(this.ticketForm.get('browser')?.value);
    const filteredBrowsers = currentBrowsers.filter(v => this.browserOptions.includes(v));

    this.ticketForm.patchValue({
      browser: filteredBrowsers.length ? filteredBrowsers : (this.browserOptions[0] ? [this.browserOptions[0]] : []),
    });

    this.refreshApplicationDependentOptions(this.ticketForm.get('application')?.value);
  }

  onApplicationChange(value: string): void {
    this.refreshApplicationDependentOptions(value);
  }

  private updateBrowserOptions(value: string): void {
    if (value === 'Web') {
      this.browserOptions = this.sortStrings(SUPPORT_TICKET_BROWSER);
    } else if (value === 'Mobile') {
      this.browserOptions = this.sortStrings(SUPPORT_TICKET_MOBILE_MODELS);
    } else {
      this.browserOptions = this.sortStrings(SUPPORT_TICKET_BROWSER_AND_MOBILE_MODELS);
    }
  }

  private refreshApplicationDependentOptions(application: string): void {
    this.functionalAreaOptions = this.getFunctionalAreaOptionsForApplication(application);
    this.moduleScreenOptions = this.getModuleScreenOptionsForApplication(application);

    const currentFunctionalAreas = this.toArray(this.ticketForm.get('functionalArea')?.value);
    const filteredFunctionalAreas = currentFunctionalAreas.filter(v => this.functionalAreaOptions.includes(v));
    const validModuleIds = new Set(this.moduleScreenOptions.map(m => String(m.id)));
    const currentModuleScreens: any[] = this.ticketForm.get('moduleScreens')?.value || [];
    const filteredModuleScreens = currentModuleScreens.filter(v => validModuleIds.has(String(v)));

    this.ticketForm.patchValue({
      functionalArea: filteredFunctionalAreas,
      moduleScreens: filteredModuleScreens,
    });

    this.functionalAreaFilterText = '';
    this.filteredFunctionalAreaOptions = [...this.functionalAreaOptions];
    this.moduleScreenFilterText = '';
    this.filteredModuleScreenOptions = [...this.moduleScreenOptions];
  }

  private getFunctionalAreaOptionsForApplication(application: string): string[] {
    if (application === 'Chemical Manager') {
      return this.sortStrings(this.uniqueStrings(SUPPORT_TICKET_CHEMICAL_MANAGER_OPTIONS));
    }
    if (application === 'Equipment Manager') {
      return this.sortStrings(this.uniqueStrings(SUPPORT_TICKET_EQUIPMENT_MANAGER_OPTIONS));
    }
    return this.getInventoryFunctionalAreaOptions(this.ticketForm.get('userInterface')?.value);
  }

  private getModuleScreenOptionsForApplication(application: string): any[] {
    if (application === 'Chemical Manager') {
      return this.createModuleScreenOptions(SUPPORT_TICKET_CHEMICAL_MANAGER_OPTIONS, 1000);
    }
    if (application === 'Equipment Manager') {
      return this.createModuleScreenOptions(SUPPORT_TICKET_EQUIPMENT_MANAGER_OPTIONS, 2000);
    }
    return this.sortByLabel(SUPPORT_TICKET_MODULES_LIST, m => m.name);
  }

  private getInventoryFunctionalAreaOptions(value: string): string[] {
    if (value === 'Web') {
      return this.sortStrings(SUPPORT_TICKET_FUNCTIONAL_AREA_WEB);
    }
    if (value === 'Mobile') {
      return this.sortStrings(SUPPORT_TICKET_FUNCTIONAL_AREA_MOBILE);
    }
    return this.sortStrings(SUPPORT_TICKET_FUNCTIONAL_AREA_BOTH);
  }

  private createModuleScreenOptions(values: string[], idStart: number): any[] {
    return this.sortStrings(this.uniqueStrings(values)).map((name, index) => ({
      id: idStart + index + 1,
      name,
    }));
  }
  // ------------------------------------------------------------------------
  // FUNCTIONAL AREA SEARCH
  // ------------------------------------------------------------------------

  filterFunctionalArea(text: string): void {
    this.functionalAreaFilterText = text || '';
    const q = this.functionalAreaFilterText.trim().toLowerCase();
    if (!q) {
      this.filteredFunctionalAreaOptions = [...this.functionalAreaOptions];
      return;
    }
    this.filteredFunctionalAreaOptions = this.functionalAreaOptions.filter(f =>
      (f || '').toLowerCase().includes(q)
    );
  }

  onFunctionalAreaPanelOpened(opened: boolean): void {
    if (opened) {
      this.functionalAreaFilterText = '';
      this.filteredFunctionalAreaOptions = [...this.functionalAreaOptions];
    }
  }
  // ------------------------------------------------------------------------
  // MODULE / SCREEN SEARCH
  // ------------------------------------------------------------------------

  filterModuleScreen(text: string): void {
    this.moduleScreenFilterText = text || '';
    const q = this.moduleScreenFilterText.trim().toLowerCase();
    if (!q) {
      this.filteredModuleScreenOptions = [...this.moduleScreenOptions];
      return;
    }
    this.filteredModuleScreenOptions = this.moduleScreenOptions.filter(m =>
      (m.name || '').toLowerCase().includes(q)
    );
  }

  onModuleScreenPanelOpened(opened: boolean): void {
    if (opened) {
      this.moduleScreenFilterText = '';
      this.filteredModuleScreenOptions = [...this.moduleScreenOptions];
    }
  }

  // ------------------------------------------------------------------------
  // FILE UPLOAD (multi — up to 5, allowed file types, <=10MB each)
  // ------------------------------------------------------------------------

  /** Bound to the dropzone click handler — short-circuited when the limit is reached. */
  onDropzoneClick(fileInput: HTMLInputElement): void {
    if (this.isUploadFull) {
      // Soft toast so the user understands why nothing happened.
      this.toastService.show('info', 'Upload limit reached',
        `You can attach a maximum of ${this.MAX_FILES} files.`, true, 4000);
      return;
    }
    fileInput.click();
  }

  onDropzoneDragOver(event: DragEvent): void {
    // Disable drop styling/behaviour when full.
    if (this.isUploadFull) {
      event.dataTransfer && (event.dataTransfer.dropEffect = 'none');
    }
    event.preventDefault();
  }

  onFileSelected(event: any): void {
    if (this.isUploadFull) {
      if (event?.target) event.target.value = '';
      return;
    }
    const files: FileList | undefined = event?.target?.files;
    if (files && files.length) void this.handleFiles(Array.from(files));
    if (event?.target) event.target.value = '';
  }

  onFileDrop(event: DragEvent): void {
    event.preventDefault();
    if (this.isUploadFull) return;
    const files = event.dataTransfer?.files;
    if (files && files.length) void this.handleFiles(Array.from(files));
  }

  private async handleFiles(files: File[]): Promise<void> {
    const validFiles: File[] = [];
    for (const file of files) {
      if (!this.isAcceptedFile(file)) {
        this.toastService.show('error', 'Invalid file type',
          'Allowed: images, PDF, DOC, DOCX, TXT, XLS, XLSX, or videos.', true, 5000);
        continue;
      }
      if (file.size / (1024 * 1024) > this.MAX_FILE_SIZE_MB) {
        this.toastService.show('error', 'File too large',
          `Max ${this.MAX_FILE_SIZE_MB} MB per file.`, true, 5000);
        continue;
      }
      validFiles.push(file);
    }

    if (!validFiles.length) return;

    const attachmentCandidates: Array<AttachedFile | File> = [...this.attachedFiles, ...validFiles];
    const cappedAttachmentCandidates = attachmentCandidates.slice(-this.MAX_FILES);

    if (attachmentCandidates.length > this.MAX_FILES) {
      const limitMessage =
        `You can upload a maximum of ${this.MAX_FILES} files. Only the latest ${this.MAX_FILES} files will be attached.`;
      this.toastService.show('error', 'Upload limit reached', limitMessage, true, 5000);
    }

    const currentBatch = ++this.fileReadBatch;
    try {
      const nextAttachedFiles = await Promise.all(
        cappedAttachmentCandidates.map(candidate =>
          this.isAttachedFile(candidate) ? Promise.resolve(candidate) : this.readFile(candidate)
        )
      );
      if (currentBatch === this.fileReadBatch) {
        this.attachedFiles = nextAttachedFiles;
      }
    } catch {
      if (currentBatch === this.fileReadBatch) {
        this.toastService.show('error', 'File upload failed',
          'Could not read one or more selected files. Please try again.', true, 5000);
      }
    }
  }

  private isAttachedFile(candidate: AttachedFile | File): candidate is AttachedFile {
    return !!(candidate as AttachedFile).file && typeof (candidate as AttachedFile).base64 === 'string';
  }

  private isAcceptedFile(file: File): boolean {
    const fileType = `${file.type || ''}`.toLowerCase();
    const fileName = `${file.name || ''}`.toLowerCase();

    return SUPPORT_TICKET_ACCEPTED_FILE_TYPES.includes(fileType)
      || SUPPORT_TICKET_ACCEPTED_FILE_EXTENSIONS.some(extension => fileName.endsWith(extension));
  }

  private readFile(file: File): Promise<AttachedFile> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve({
        file,
        name: file.name,
        type: file.type,
        size: file.size,
        base64: (reader.result as string) || '',
      });
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
  }

  removeFile(index: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    if (index < 0 || index >= this.attachedFiles.length) return;
    this.fileReadBatch++;
    this.attachedFiles.splice(index, 1);
  }

  fileIconFor(file: AttachedFile): string {
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'videocam';
    if (file.type === 'application/pdf') return 'picture_as_pdf';
    return 'description';
  }

  fileSizeLabel(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }

  isInvalid(controlName: string): boolean {
    const c = this.ticketForm.get(controlName);
    if (!c) return false;
    return c.invalid && (c.touched || c.dirty);
  }

  // ------------------------------------------------------------------------
  // SUBMIT
  // ------------------------------------------------------------------------

  /**
   * Builds the JSON body for POST /supportTicket/SubmitDefect.
   * Key names follow the backend contract supplied by the user.
   *  - Multi-select fields (functionalArea, moduleScreen) are comma-joined.
   *  - When `submittingFor` is OTHERS, the picked user ids are comma-joined.
    *  - issueOccurredDate is sent as YYYY-MM-DDTHH:mm:ss+HH:mm.
   */
  private buildSubmitDefectPayload(): any {
    const v = this.ticketForm.getRawValue();

    const faArr: string[] = Array.isArray(v.functionalArea) ? v.functionalArea : (v.functionalArea ? [v.functionalArea] : []);
    const browserArr: string[] = this.toArray(v.browser);
    const msIds: number[] = Array.isArray(v.moduleScreens) ? v.moduleScreens : (v.moduleScreens != null ? [v.moduleScreens] : []);
    const msNames: string[] = msIds
      .map(id => this.moduleScreenOptions.find(m => m.id === id)?.name)
      .filter((n): n is string => !!n);

    // submittingFor: MYSELF -> logged-in user id; OTHERS -> comma-joined picked ids
    let submittingFor: string = this.currentLoggedInUserId ? String(this.currentLoggedInUserId) : '';
    if (v.submittingFor === 'OTHERS') {
      submittingFor = this.selectedOtherUsers.map(u => u.id).join(',');
    }

    return {
      loggedInOrgId: this.toNum(this.currentLoggedInOrgId),
      loggedInStockroomId: this.toNum(this.currentLoggedInStockroomId),
      reportingUserId: this.toNum(this.currentLoggedInUserId),
      orgId: this.toNum(v.orgId),
      stockroomId: this.toNum(v.stockroomId),
      submittingFor,
      location: v.location != null ? String(v.location) : null,
      userInterface: v.userInterface,                  // 'Web' | 'Mobile' | 'Both'
      functionalArea: faArr.join(','),                 // multi-select -> CSV
      browser: browserArr.join(','),                   // multi-select -> CSV
      applications: v.application,                     // backend key is "applications"
      issuesOccursForAllUsers: !!v.issueOccursForAllUsers, // backend key has trailing 's'
      issueOccurredDate: v.issueOccurredDate ? this.formatDateWithOffset(v.issueOccurredDate) : null,
      issueType: v.issueType,
      screenshotPresent: this.attachedFiles.length > 0 ? true : false,
      scaleOfImpact: v.scaleOfImpact,
      moduleScreen: msNames.join(','),                 // multi-select -> CSV
      defectTitle: v.defectTitle,
      defectDescription: v.defectDescription,
      defectStepsToReproduce: v.defectStepsToReproduce,
    };
  }

  /** Looks for a ticket id in common response shapes. */
  private extractSupportTicketId(res: any): string | number | null {
    if (res == null) return null;
    const direct = res.data ?? res.data ?? "";
    if (direct != null) return direct;
    if (res.data && typeof res.data === 'object') {
      return res.data ?? res.data ?? res.data ?? null;
    }
    return null;
  }

  async submit(): Promise<void> {
    this.ticketForm.markAllAsTouched();
    if (this.ticketForm.invalid) {
      this.toastService.show('error', 'Required fields missing',
        'Please fill all required fields and try again.', true, 5000);
      return;
    }
    // For OTHERS, require at least one user in the table.
    if (this.ticketForm.get('submittingFor')?.value === 'OTHERS' && !this.selectedOtherUsers.length) {
      this.toastService.show('error', 'No user selected',
        'Please add at least one user to the list.', true, 5000);
      return;
    }

    const payload = this.buildSubmitDefectPayload();
    // eslint-disable-next-line no-console
    console.log('[SupportTicketForm] step 1 — submitDefect payload:', payload);

    try {
      this.submitting = true;

      // ---- Step 1: submit defect details ----
      const res = await this.supportTicketService.submitDefect(payload);
      // eslint-disable-next-line no-console
      console.log('[SupportTicketForm] step 1 response:', res);

      const ticketId = this.extractSupportTicketId(res);
      if (!ticketId) {
        // Treat as success but warn — the screenshots cannot be uploaded
        // without an id. Show a partial-success toast.
        this.toastService.show('success', res.status,
          '', true, 6000);
        this.dialogRef.close({ submitted: true, payload, ticketId: null, screenshotsUploaded: false });
        return;
      }

      // ---- Step 2: upload screenshots (skip if none attached) ----
      let screenshotsUploaded = false;
      if (this.attachedFiles.length) {
        try {
          const files = this.attachedFiles.map(f => f.file);
          const upRes = await this.supportTicketService.uploadDefectScreenshots(ticketId, files);
          // eslint-disable-next-line no-console
          console.log('[SupportTicketForm] step 2 — uploadDefectScreenshots response:', upRes);
          screenshotsUploaded = true;
        } catch (upErr) {
          console.error('[SupportTicketForm] step 2 — upload error', upErr);
          this.toastService.show('error', 'Screenshots upload failed',
            'Ticket was saved but screenshots could not be uploaded.', true, 6000);
          this.dialogRef.close({ submitted: true, payload, ticketId, screenshotsUploaded: false });
          return;
        }
      }

      this.toastService.show('success', 'Support ticket submitted',
        'Your support ticket has been submitted successfully.', true, 5000);
      this.dialogRef.close({ submitted: true, payload, ticketId, screenshotsUploaded });
      this.router.navigate(['/app']);
    } catch (err) {
      console.error('[SupportTicket] submit error', err);
      this.toastService.show('error', 'Submit failed',
        'Could not submit the support ticket. Please try again.', true, 5000);
    } finally {
      this.submitting = false;
    }
  }

  onClose(): void {
    this.dialogRef.close();
  }

  /** YYYY-MM-DDTHH:mm:ss+HH:mm for the SubmitDefect payload. */
  private formatDateWithOffset(d: Date | string): string {
    const selectedDate = d instanceof Date ? d : new Date(d);
    if (isNaN(selectedDate.getTime())) return '';

    const now = new Date();
    const mm = String(selectedDate.getMonth() + 1).padStart(2, '0');
    const dd = String(selectedDate.getDate()).padStart(2, '0');
    const yyyy = selectedDate.getFullYear();
    const hh = String(now.getHours()).padStart(2, '0');
    const min = String(now.getMinutes()).padStart(2, '0');
    const ss = String(now.getSeconds()).padStart(2, '0');
    const tzTotalMinutes = -now.getTimezoneOffset();
    const tzSign = tzTotalMinutes >= 0 ? '+' : '-';
    const tzAbsMinutes = Math.abs(tzTotalMinutes);
    const tzHours = String(Math.floor(tzAbsMinutes / 60)).padStart(2, '0');
    const tzMinutes = String(tzAbsMinutes % 60).padStart(2, '0');

    return `${yyyy}-${mm}-${dd}T${hh}:${min}:${ss}${tzSign}${tzHours}:${tzMinutes}`;
  }

  /** Convert a value to a number; returns null if not parseable. */
  private toNum(v: any): number | null {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return isNaN(n) ? null : n;
  }

  private toArray(value: any): string[] {
    if (Array.isArray(value)) return value;
    return value ? [value] : [];
  }

  private sortStrings(values: string[]): string[] {
    return [...values].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }

  private uniqueStrings(values: string[]): string[] {
    return Array.from(new Set(values.map(v => (v || '').trim()).filter(Boolean)));
  }

  private sortByLabel<T>(values: T[], labelGetter: (value: T) => string): T[] {
    return [...values].sort((a, b) =>
      (labelGetter(a) || '').localeCompare(labelGetter(b) || '', undefined, { sensitivity: 'base' })
    );
  }

  private getOrganizationName(org: any): string {
    return (org?.orgName || org?.organizationName || org?.name || '').toString();
  }

  private getStockroomName(stockroom: any): string {
    return (stockroom?.stockroomName || stockroom?.name || '').toString();
  }

  getMultiSelectLabel(controlName: string): string {
    const selected = this.toArray(this.ticketForm?.get(controlName)?.value);
    if (!selected.length) return '';

    const extraCount = selected.length - 1;
    return extraCount > 0 ? `${selected[0]} + ${extraCount}` : selected[0];
  }

  getModuleScreenSelectLabel(): string {
    const selectedIds: any[] = this.ticketForm?.get('moduleScreens')?.value || [];
    if (!selectedIds.length) return '';

    const firstModule = this.moduleScreenOptions.find(m => String(m.id) === String(selectedIds[0]));
    const firstLabel = firstModule?.name || '';
    const extraCount = selectedIds.length - 1;
    return extraCount > 0 ? `${firstLabel} + ${extraCount}` : firstLabel;
  }

  cancel() {
    const confirm = this.dialogService.openConfirmDialog(
      this.translatePipe.transform('ime.sure.want.to.cancel'),
      this.translatePipe.transform('ime.any.information.entered.desc'),
      this.translatePipe.transform('yes'),
      this.translatePipe.transform('no')
    );
    confirm.afterClosed().subscribe((response: any) => {
      if (response.data) {
        this.dialogRef.close();
      }
    });
  }
}
